import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, IconButton, Tooltip, useTheme } from '@mui/material';
import styled, { CSSObject } from '@emotion/styled';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { ShowImageButton } from '../../invitation/Detail/Tabs/ShowImageButton';

const FileCard = styled.div(
  ({ theme }: any): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
    border: `1px solid ${theme.palette.divider}`,
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.hover, 0.08),
    },
  }),
);

const FileListContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  }),
);

const getFileIcon = (fileName: string | undefined) => {
  if (!fileName) return InsertDriveFileOutlinedIcon;
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext ?? '')) return ImageOutlinedIcon;
  if (ext === 'pdf') return PictureAsPdfOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
};

export interface QcsFileItem {
  id: string;
  fileName: string;
  documentUrl: string;
}

interface Props {
  items: QcsFileItem[];
  onRemove?: (item: QcsFileItem) => void;
  disabled?: boolean;
}

export const QcsFileList: FC<Props> = ({ items, onRemove, disabled }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!items.length) return null;

  return (
    <FileListContainer>
      {items.map((item) => {
        const FileIcon = getFileIcon(item.fileName);
        const displayName = decodeURIComponent(item.fileName.replace(/\+/g, ' '));

        return (
          <FileCard key={item.id}>
            <FileIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
            <ShowImageButton
              documentUrl={item.documentUrl}
              fileName={item.fileName}
              variant="text"
              sx={{
                flex: 1,
                justifyContent: 'flex-start',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textTransform: 'none',
                padding: 0,
                minWidth: 0,
              }}
            >
              {displayName}
            </ShowImageButton>
            {onRemove && (
              <Tooltip title={t('common.delete')}>
                <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => onRemove(item)}
                    disabled={disabled}
                    sx={{
                      color: disabled ? theme.palette.action.disabled : theme.palette.error.main,
                      opacity: 0.7,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </FileCard>
        );
      })}
    </FileListContainer>
  );
};